import { useCallback, useEffect, useMemo, useState } from "react";
import { FileText, RefreshCw } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { MultiSelect } from "@/components/ui/MultiSelect";
import { useCostCenters } from "@/hooks/useCostCenters";
import { useModalFormSession } from "@/hooks/useModalFormSession";
import { cn } from "@/utils/cn";
import { formatDateTime, formatNumber } from "@/utils/format";
import {
  CONTRATO_STATUS_LABELS,
  extractApiError,
  fetchLocacoesClientesSienge,
  isContratoModeloMensalEquipamento,
  isContratoPorCarradas,
  isContratoPorHoras,
  isEquipamentoAtivo,
  modelosContratoPorTipo,
  normalizeAtivoTipo,
  resolveAtivoTipo,
  sortVigenciasValorCarradaDesc,
  vigenciaValorEstaAberta,
  type AtivoTipo,
  type ContratoDto,
  type ContratoFormValues,
  type ContratoStatus,
  type MaquinarioDto,
  type SiengeClienteOption,
  type ValorCarradaVigencia,
} from "@/services/locacoes";
import { listMaquinaIdsEmContratoAtivo } from "./locacoesUtils";
import { upperLocacaoText } from "./upperLocacaoText";
import { ContratoTipoTabs } from "./ContratoTipoTabs";

function isCaminhaoMaquinario(m: Pick<MaquinarioDto, "tipo" | "tipoAtivo">): boolean {
  if (resolveAtivoTipo(m) === "equipamento") return false;
  const tipo = (m.tipo ?? "").trim();
  if (tipo === "Caminhão Basculante" || tipo === "Caminhão Pipa") return true;
  return /^caminh[aã]o\b/i.test(tipo);
}

function initialVigencia(valorUnitario = 0): ValorCarradaVigencia {
  const now = new Date().toISOString();
  return {
    id: `vcv-local-${Date.now().toString(36)}`,
    valorUnitario,
    vigenteDesde: now,
    vigenteAte: null,
    observacao: "",
  };
}

interface Props {
  open: boolean;
  editing: ContratoDto | null;
  /** Aba inicial ao criar contrato (vinda da listagem). */
  initialTipoContrato?: AtivoTipo;
  maquinarios: MaquinarioDto[];
  contratos: ContratoDto[];
  busy?: boolean;
  onClose: () => void;
  onSave: (values: ContratoFormValues, editingId: string | null) => Promise<void>;
  onDelete?: () => void;
  onMutationComplete?: () => void | Promise<void>;
}

function emptyForm(tipo: AtivoTipo = "maquinario"): ContratoFormValues {
  const today = new Date().toISOString().slice(0, 10);
  const modelos = modelosContratoPorTipo(tipo);
  return {
    modeloContrato: modelos[0] ?? "",
    clienteSiengeId: "",
    clienteSiengeNome: "",
    clienteSiengeDocument: "",
    valorContrato: 0,
    valorHora: 0,
    valorHoraPorMaquina: {},
    valorCarradaVigencias: [initialVigencia()],
    valorMensal: 0,
    tipoContrato: tipo,
    maquinaIds: [],
    infoBasicos: "",
    costCenterId: "",
    costCenterLabel: "",
    dataInicio: today,
    dataFimPrevista: "",
    combustivelPagoCliente: false,
  };
}

export function ModalContrato({
  open,
  editing,
  initialTipoContrato = "maquinario",
  maquinarios,
  contratos,
  busy,
  onClose,
  onSave,
  onDelete,
  onMutationComplete,
}: Props) {
  const [values, setValues] = useState<ContratoFormValues>(emptyForm());
  const [justificativaAlteracao, setJustificativaAlteracao] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [clientes, setClientes] = useState<SiengeClienteOption[]>([]);
  const [clientesTotal, setClientesTotal] = useState(0);
  const [loadingClientes, setLoadingClientes] = useState(false);
  const [clientesError, setClientesError] = useState<string | null>(null);
  const { options: ccOptions, loading: ccLoading } = useCostCenters();

  const tipoContrato = normalizeAtivoTipo(values.tipoContrato ?? initialTipoContrato);
  const isEquipamentoContrato = tipoContrato === "equipamento";
  const porHoras = !isEquipamentoContrato && isContratoPorHoras(values.modeloContrato);
  const porCarradas = !isEquipamentoContrato && isContratoPorCarradas(values.modeloContrato);
  const mensalEquipamento =
    isEquipamentoContrato && isContratoModeloMensalEquipamento(values.modeloContrato);

  const modeloOptions = useMemo(
    () => modelosContratoPorTipo(tipoContrato).map((m) => ({ value: m, label: m })),
    [tipoContrato],
  );

  const maquinaIdsOcupadas = useMemo(
    () => listMaquinaIdsEmContratoAtivo(contratos, editing?.id),
    [contratos, editing?.id],
  );

  const maquinaOptions = useMemo(
    () =>
      maquinarios
        .filter((m) => {
          if (resolveAtivoTipo(m) !== tipoContrato) return false;
          if (porCarradas && !isCaminhaoMaquinario(m)) return false;
          return !maquinaIdsOcupadas.has(m.id) || values.maquinaIds.includes(m.id);
        })
        .map((m) => ({
          value: m.id,
          label: `${m.nome} (${m.codigoInterno || "—"})${isEquipamentoAtivo(m) ? ` · ${m.tipo || "Equipamento"}` : ""}`,
        })),
    [maquinarios, maquinaIdsOcupadas, values.maquinaIds, tipoContrato, porCarradas],
  );

  const loadClientes = useCallback(async (refresh = false) => {
    setLoadingClientes(true);
    setClientesError(null);
    try {
      const result = await fetchLocacoesClientesSienge({ refresh });
      setClientes(result.clientes);
      setClientesTotal(result.totalCount);
    } catch (e) {
      setClientes([]);
      setClientesTotal(0);
      setClientesError(extractApiError(e, "Não foi possível carregar os clientes do Sienge."));
    } finally {
      setLoadingClientes(false);
    }
  }, []);

  const formSessionKey = `${editing?.id ?? "new"}::${initialTipoContrato}`;

  useModalFormSession(open, formSessionKey, () => {
    if (editing) {
      const tipo = normalizeAtivoTipo(editing.tipoContrato);
      setValues({
        modeloContrato: editing.modeloContrato,
        clienteSiengeId: editing.clienteSiengeId,
        clienteSiengeNome: editing.clienteSiengeNome,
        clienteSiengeDocument: editing.clienteSiengeDocument,
        valorContrato: editing.valorContrato,
        valorHora: editing.valorHora ?? 0,
        valorHoraPorMaquina: Object.fromEntries(
          editing.maquinaIds.map((id) => [
            id,
            editing.valorHoraPorMaquina?.[id] ?? (editing.valorHora > 0 ? editing.valorHora : 0),
          ]),
        ),
        valorCarradaVigencias:
          editing.valorCarradaVigencias && editing.valorCarradaVigencias.length > 0
            ? editing.valorCarradaVigencias.map((v) => ({
                id: v.id || `vcv-${v.vigenteDesde || v.dataInicio || "x"}`,
                valorUnitario: v.valorUnitario,
                vigenteDesde: v.vigenteDesde || (v.dataInicio ? `${v.dataInicio}T00:00:00.000Z` : new Date().toISOString()),
                vigenteAte: v.vigenteAte ?? (v.dataFim ? `${v.dataFim}T23:59:59.999Z` : null),
                observacao: v.observacao ?? "",
                ...(v.registradoPorNome ? { registradoPorNome: v.registradoPorNome } : {}),
              }))
            : [initialVigencia()],
        valorMensal: editing.valorMensal ?? 0,
        tipoContrato: tipo,
        maquinaIds: [...editing.maquinaIds],
        infoBasicos: upperLocacaoText(editing.infoBasicos),
        costCenterId: editing.costCenterId,
        costCenterLabel: editing.costCenterLabel,
        dataInicio: editing.dataInicio,
        dataFimPrevista: editing.dataFimPrevista ?? "",
        status: editing.status,
        combustivelPagoCliente: editing.combustivelPagoCliente === true,
      });
    } else {
      setValues(emptyForm(initialTipoContrato));
    }
    setJustificativaAlteracao("");
    setError(null);
  });

  useEffect(() => {
    if (!open) return;
    void loadClientes(true);

    const timer = window.setInterval(() => {
      void loadClientes(true);
    }, 90_000);

    return () => window.clearInterval(timer);
  }, [open, loadClientes]);

  const clienteOptions = useMemo(() => {
    const opts = clientes.map((c) => ({
      value: c.id,
      label: c.documentNumber ? `${c.name} (${c.documentNumber})` : c.name,
    }));
    if (editing?.clienteSiengeId && !opts.some((o) => o.value === editing.clienteSiengeId)) {
      opts.unshift({
        value: editing.clienteSiengeId,
        label: editing.clienteSiengeDocument
          ? `${editing.clienteSiengeNome} (${editing.clienteSiengeDocument})`
          : editing.clienteSiengeNome,
      });
    }
    return opts;
  }, [clientes, editing]);

  const handleTipoTabChange = (tipo: AtivoTipo) => {
    if (editing) return;
    const modelos = modelosContratoPorTipo(tipo);
    setValues((v) => ({
      ...emptyForm(tipo),
      clienteSiengeId: v.clienteSiengeId,
      clienteSiengeNome: v.clienteSiengeNome,
      clienteSiengeDocument: v.clienteSiengeDocument,
      costCenterId: v.costCenterId,
      costCenterLabel: v.costCenterLabel,
      dataInicio: v.dataInicio,
      dataFimPrevista: v.dataFimPrevista,
      infoBasicos: v.infoBasicos,
      modeloContrato: modelos.includes(v.modeloContrato as (typeof modelos)[number])
        ? v.modeloContrato
        : (modelos[0] ?? ""),
    }));
    setError(null);
  };

  const handleModeloChange = (modeloContrato: string) => {
    const porHorasNovo = isContratoPorHoras(modeloContrato);
    const porCarradasNovo = isContratoPorCarradas(modeloContrato);
    const mensalNovo = isEquipamentoContrato && isContratoModeloMensalEquipamento(modeloContrato);
    setValues((v) => ({
      ...v,
      modeloContrato,
      maquinaIds: porCarradasNovo
        ? v.maquinaIds.filter((id) => {
            const m = maquinarios.find((x) => x.id === id);
            return m ? isCaminhaoMaquinario(m) : false;
          })
        : v.maquinaIds,
      ...(porHorasNovo
        ? { valorContrato: 0, valorMensal: 0, valorCarradaVigencias: [] }
        : porCarradasNovo
          ? {
              valorContrato: 0,
              valorMensal: 0,
              valorHora: 0,
              valorHoraPorMaquina: {},
              valorCarradaVigencias: [
                initialVigencia(v.valorCarradaVigencias[0]?.valorUnitario ?? 0),
              ],
            }
          : mensalNovo
            ? { valorContrato: 0, valorHora: 0, valorHoraPorMaquina: {}, valorCarradaVigencias: [] }
            : { valorMensal: 0, valorHora: 0, valorHoraPorMaquina: {}, valorCarradaVigencias: [] }),
    }));
  };

  const handleValorInicialCarradaChange = (raw: string) => {
    const valorUnitario = Number(raw) || 0;
    setValues((v) => ({
      ...v,
      valorCarradaVigencias: [initialVigencia(valorUnitario)],
    }));
  };

  const handleMaquinaIdsChange = (maquinaIds: string[]) => {
    setValues((v) => {
      const valorHoraPorMaquina: Record<string, number> = {};
      for (const id of maquinaIds) {
        valorHoraPorMaquina[id] = v.valorHoraPorMaquina[id] ?? 0;
      }
      return { ...v, maquinaIds, valorHoraPorMaquina };
    });
  };

  const handleValorHoraMaquinaChange = (maquinaId: string, raw: string) => {
    const valor = Number(raw) || 0;
    setValues((v) => ({
      ...v,
      valorHoraPorMaquina: { ...v.valorHoraPorMaquina, [maquinaId]: valor },
    }));
  };

  const maquinasSelecionadas = useMemo(
    () =>
      values.maquinaIds
        .map((id) => maquinarios.find((m) => m.id === id))
        .filter((m): m is MaquinarioDto => m != null),
    [values.maquinaIds, maquinarios],
  );

  const handleSubmit = async () => {
    setError(null);
    if (!values.clienteSiengeId) {
      setError("Selecione o cliente no Sienge.");
      return;
    }
    if (values.maquinaIds.length === 0) {
      setError(`Selecione ao menos um ${isEquipamentoContrato ? "equipamento" : "maquinário"}.`);
      return;
    }
    const tipoIncompativel = values.maquinaIds.some(
      (id) => resolveAtivoTipo(maquinarios.find((m) => m.id === id) ?? { tipoAtivo: "maquinario", tipo: "" }) !== tipoContrato,
    );
    if (tipoIncompativel) {
      setError("Os ativos selecionados não correspondem ao tipo de contrato.");
      return;
    }
    const emOutroContrato = values.maquinaIds.filter((id) => maquinaIdsOcupadas.has(id));
    if (emOutroContrato.length > 0) {
      const nomes = emOutroContrato
        .map((id) => maquinarios.find((m) => m.id === id)?.nome ?? id)
        .join(", ");
      setError(`Ativo(s) já vinculado(s) a outro contrato ativo: ${nomes}.`);
      return;
    }
    if (mensalEquipamento) {
      if (!values.valorMensal || values.valorMensal <= 0) {
        setError("Informe o valor mensal do equipamento.");
        return;
      }
    } else if (porHoras) {
      const semValor = values.maquinaIds.filter((id) => (values.valorHoraPorMaquina[id] ?? 0) <= 0);
      if (semValor.length > 0) {
        setError("Informe o valor da hora para cada máquina selecionada.");
        return;
      }
    } else if (porCarradas) {
      const temHistorico = values.valorCarradaVigencias.some((v) => v.valorUnitario > 0);
      if (!temHistorico) {
        setError("Informe o valor unitário negociado por carrada.");
        return;
      }
      const naoCaminhao = values.maquinaIds.filter((id) => {
        const m = maquinarios.find((x) => x.id === id);
        return !m || !isCaminhaoMaquinario(m);
      });
      if (naoCaminhao.length > 0) {
        setError("Contratos por carradas aceitam apenas caminhões.");
        return;
      }
    } else if (!values.valorContrato || values.valorContrato <= 0) {
      setError("Informe o valor do contrato.");
      return;
    }
    if (editing && !justificativaAlteracao.trim()) {
      setError("Informe a justificativa da alteração.");
      return;
    }
    try {
      await onSave(
        {
          ...values,
          tipoContrato,
          valorContrato: porHoras || porCarradas || mensalEquipamento ? 0 : values.valorContrato,
          valorMensal: mensalEquipamento ? values.valorMensal : 0,
          valorHora: 0,
          valorHoraPorMaquina: porHoras ? values.valorHoraPorMaquina : {},
          valorCarradaVigencias: porCarradas
            ? (() => {
                const jaTemHistorico =
                  !!editing &&
                  (editing.valorCarradaVigencias?.some((v) => v.valorUnitario > 0) ?? false);
                if (jaTemHistorico) {
                  return values.valorCarradaVigencias.map((v) => ({
                    id: v.id,
                    valorUnitario: v.valorUnitario,
                    vigenteDesde: v.vigenteDesde,
                    vigenteAte: v.vigenteAte,
                    ...(v.observacao?.trim() ? { observacao: v.observacao.trim() } : {}),
                    ...(v.registradoPorId ? { registradoPorId: v.registradoPorId } : {}),
                    ...(v.registradoPorNome ? { registradoPorNome: v.registradoPorNome } : {}),
                  }));
                }
                return [
                  {
                    id: values.valorCarradaVigencias[0]?.id || `vcv-local-${Date.now().toString(36)}`,
                    vigenteDesde: new Date().toISOString(),
                    vigenteAte: null,
                    valorUnitario: values.valorCarradaVigencias[0]?.valorUnitario ?? 0,
                  },
                ];
              })()
            : [],
          ...(editing ? { justificativaAlteracao: justificativaAlteracao.trim() } : {}),
        },
        editing?.id ?? null,
      );
      await onMutationComplete?.();
      onClose();
    } catch (e: unknown) {
      setError(extractApiError(e, "Erro ao salvar contrato."));
    }
  };

  const ativosLabel = isEquipamentoContrato ? "Equipamentos" : "Maquinários";
  const ativosPlaceholder = isEquipamentoContrato
    ? "Selecione equipamentos…"
    : "Selecione maquinários…";

  const title = (
    <div className="flex items-center gap-3">
      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-50">
        <FileText className="h-4 w-4 text-brand-600" />
      </div>
      <span className="font-bold text-slate-900">{editing ? "Editar contrato" : "Novo contrato"}</span>
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="lg"
      footer={
        <div className="flex w-full flex-wrap items-center justify-between gap-2">
          <div>
            {editing && onDelete && (
              <Button type="button" variant="secondary" onClick={onDelete} disabled={busy}>
                Excluir
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>
              Cancelar
            </Button>
            <Button type="button" onClick={() => void handleSubmit()} loading={busy}>
              Salvar
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-800">{error}</div>
        )}

        {editing?.codigoContrato && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Código do contrato</p>
            <p className="mt-0.5 text-sm font-bold text-slate-900">{editing.codigoContrato}</p>
          </div>
        )}

        <div className="space-y-1.5">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Tipo de contrato *</span>
          <ContratoTipoTabs
            value={tipoContrato}
            onChange={handleTipoTabChange}
            disabled={!!editing}
          />
        </div>

        <Select
          label="Modelo de contrato *"
          value={values.modeloContrato}
          onChange={(e) => handleModeloChange(e.target.value)}
          options={modeloOptions}
        />
        {isEquipamentoContrato && (
          <p className="rounded-lg border border-violet-200 bg-violet-50 px-3 py-2 text-sm text-violet-900">
            Contrato de equipamento: horímetro, abastecimento e operadores não se aplicam. O modelo por horímetro não
            está disponível neste tipo.
          </p>
        )}
        {porHoras && (
          <p className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-900">
            Neste modelo o faturamento é pelo consumo em horas. Informe o valor acordado por hora de cada máquina — o
            total será calculado com base nos registros de horímetro.
          </p>
        )}
        {porCarradas && (
          <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            Locação por carradas: informe o valor unitário negociado com os operadores. Reajustes futuros são feitos
            no detalhe do contrato (Registrar reajuste), sem lançar carrada, e ficam no histórico. O faturamento usa
            quantidade × valor unitário de cada lançamento.
          </p>
        )}
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
              Cliente (Sienge) *
            </span>
            <button
              type="button"
              disabled={loadingClientes}
              onClick={() => void loadClientes(true)}
              className="inline-flex items-center gap-1 text-xs font-medium text-brand-600 hover:text-brand-700 disabled:opacity-50"
            >
              <RefreshCw className={cn("w-3.5 h-3.5", loadingClientes && "animate-spin")} />
              Atualizar lista
            </button>
          </div>
          <SearchableSelect
            value={values.clienteSiengeId}
            onChange={(id) => {
              const c = clientes.find((x) => x.id === id);
              setValues((v) => ({
                ...v,
                clienteSiengeId: id,
                clienteSiengeNome: c?.name ?? (editing?.clienteSiengeId === id ? editing.clienteSiengeNome : ""),
                clienteSiengeDocument:
                  c?.documentNumber ?? (editing?.clienteSiengeId === id ? editing.clienteSiengeDocument : ""),
              }));
            }}
            options={clienteOptions}
            placeholder={loadingClientes ? "Carregando clientes…" : "Buscar por nome ou CPF/CNPJ…"}
            hint={
              clientesError
                ? clientesError
                : loadingClientes
                  ? "Sincronizando todos os clientes cadastrados no Sienge…"
                  : clientesTotal > 0
                    ? `${clientesTotal} cliente(s) no Sienge — lista atualizada automaticamente a cada 90 s`
                    : "Nenhum cliente encontrado no Sienge"
            }
            error={clientesError ?? undefined}
            loading={loadingClientes}
            dropdownMinWidth={360}
            className="w-full"
          />
        </div>
        <MultiSelect
          label={`${porCarradas ? "Caminhões" : ativosLabel} *`}
          value={values.maquinaIds}
          onChange={handleMaquinaIdsChange}
          options={maquinaOptions}
          placeholder={porCarradas ? "Selecione caminhões…" : ativosPlaceholder}
        />
        {maquinaIdsOcupadas.size > 0 && (
          <p className="text-xs text-slate-500">Ativos já em outro contrato ativo não aparecem na lista.</p>
        )}
        {mensalEquipamento ? (
          <Input
            label="Valor mensal (R$) *"
            type="number"
            min={0}
            step="0.01"
            value={values.valorMensal || ""}
            onChange={(e) => setValues((v) => ({ ...v, valorMensal: Number(e.target.value) || 0 }))}
          />
        ) : porHoras ? (
          <div className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Valor da hora por máquina (R$) *
            </p>
            {maquinasSelecionadas.length === 0 ? (
              <p className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500">
                Selecione as máquinas acima para informar o valor/hora de cada uma.
              </p>
            ) : (
              <div className="space-y-3">
                {maquinasSelecionadas.map((m) => (
                  <Input
                    key={m.id}
                    label={`${m.nome}${m.codigoInterno ? ` (${m.codigoInterno})` : ""}`}
                    type="number"
                    min={0}
                    step="0.01"
                    value={values.valorHoraPorMaquina[m.id] || ""}
                    onChange={(e) => handleValorHoraMaquinaChange(m.id, e.target.value)}
                    placeholder="Ex.: 350,00"
                  />
                ))}
              </div>
            )}
          </div>
        ) : porCarradas ? (
          <div className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Valor por carrada (R$) *
            </p>
            {!editing ||
            values.valorCarradaVigencias.length === 0 ||
            !values.valorCarradaVigencias.some((v) => v.valorUnitario > 0) ? (
              <Input
                label="Valor unitário negociado *"
                type="number"
                min={0}
                step="0.01"
                value={values.valorCarradaVigencias[0]?.valorUnitario || ""}
                onChange={(e) => handleValorInicialCarradaChange(e.target.value)}
                placeholder="Ex.: 120,00"
                hint="Valor acordado com os operadores. Reajustes posteriores são feitos no detalhe do contrato."
              />
            ) : (
              <>
                <p className="text-xs text-slate-500">
                  Histórico somente leitura. Para reajustar o valor, abra o contrato e use &quot;Registrar reajuste&quot;.
                </p>
                <div className="overflow-x-auto rounded-lg border border-slate-200">
                  <table className="min-w-full text-left text-sm">
                    <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="px-3 py-2 font-semibold">Valor</th>
                        <th className="px-3 py-2 font-semibold">Desde</th>
                        <th className="px-3 py-2 font-semibold">Até</th>
                        <th className="px-3 py-2 font-semibold">Observação</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {[...sortVigenciasValorCarradaDesc(values.valorCarradaVigencias)].map((vig, index) => (
                          <tr key={`vig-ro-${vig.id || vig.vigenteDesde}-${index}`} className="bg-white">
                            <td className="px-3 py-2 font-medium text-slate-800">
                              R$ {formatNumber(vig.valorUnitario, 2)}
                            </td>
                            <td className="px-3 py-2 text-slate-700">
                              {formatDateTime(vig.vigenteDesde || vig.dataInicio || "")}
                            </td>
                            <td className="px-3 py-2 text-slate-700">
                              {vigenciaValorEstaAberta(vig)
                                ? "Vigente"
                                : formatDateTime(vig.vigenteAte || vig.dataFim || "")}
                            </td>
                            <td className="px-3 py-2 text-slate-600">{vig.observacao?.trim() || "—"}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        ) : (
          <Input
            label="Valor do contrato (R$) *"
            type="number"
            min={0}
            step="0.01"
            value={values.valorContrato || ""}
            onChange={(e) => setValues((v) => ({ ...v, valorContrato: Number(e.target.value) || 0 }))}
          />
        )}
        <SearchableSelect
          label="Centro de custo / obra"
          value={values.costCenterId}
          onChange={(id) => {
            const cc = ccOptions.find((o) => o.value === id);
            setValues((v) => ({
              ...v,
              costCenterId: id,
              costCenterLabel: cc?.label ?? "",
            }));
          }}
          options={ccOptions}
          placeholder={ccLoading ? "Carregando…" : "Opcional"}
          disabled={ccLoading}
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="Data início"
            type="date"
            value={values.dataInicio}
            onChange={(e) => {
              const dataInicio = e.target.value;
              setValues((v) => ({ ...v, dataInicio }));
            }}
          />
          <Input
            label="Previsão término"
            type="date"
            value={values.dataFimPrevista}
            onChange={(e) => setValues((v) => ({ ...v, dataFimPrevista: e.target.value }))}
          />
        </div>
        {editing && (
          <Select
            label="Status do contrato"
            value={values.status ?? editing.status}
            onChange={(e) => setValues((v) => ({ ...v, status: e.target.value as ContratoStatus }))}
            options={Object.entries(CONTRATO_STATUS_LABELS).map(([value, label]) => ({ value, label }))}
          />
        )}
        {editing && (
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Justificativa da alteração <span className="text-red-600">*</span>
            </label>
            <textarea
              value={justificativaAlteracao}
              onChange={(e) => setJustificativaAlteracao(upperLocacaoText(e.target.value))}
              rows={3}
              placeholder="Descreva o motivo da alteração (ex.: reajuste contratual, correção de valor/hora…)"
              className="mt-1.5 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
            />
          </div>
        )}
        {!isEquipamentoContrato && !porCarradas && (
          <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 rounded border-slate-300 text-brand-600"
              checked={values.combustivelPagoCliente === true}
              onChange={(e) =>
                setValues((v) => ({ ...v, combustivelPagoCliente: e.target.checked }))
              }
            />
            <span className="text-sm text-slate-700">
              <span className="font-medium text-slate-900">Combustível pago pelo cliente</span>
              <span className="mt-0.5 block text-xs text-slate-500">
                Neste modelo não se registram abastecimentos pela Semog e os horímetros não entram no cálculo de
                consumo (L/h).
              </span>
            </span>
          </label>
        )}
        <div>
          <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Informações básicas (editável)
          </label>
          <textarea
            value={values.infoBasicos}
            onChange={(e) => setValues((v) => ({ ...v, infoBasicos: upperLocacaoText(e.target.value) }))}
            rows={4}
            placeholder="Condições, observações, cláusulas resumidas…"
            className="mt-1.5 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
          />
        </div>
      </div>
    </Modal>
  );
}
